/**
 * ventaPromocionesResumen.ts — Agrega descuentos, precios especiales (mayorista/VIP)
 * y fidelización (puntos + premios canjeados) de las ventas del turno para mostrarlo
 * en el ticket de cierre.
 *
 * QUÉ EXPORTA: buildVentasPromocionesResumen, CierrePromocionesResumen.
 * QUÉ NO DEBE HACER: persistir ni leer de la fuente de datos.
 */

import type { Venta } from "../types/caja.types";

export interface ClientePuntosResumen {
  clientePuntosId: string;
  nombre: string;
  codigo?: string;
  puntosGanados: number;
  saldoAntes?: number;
  saldoNuevo?: number;
}

export interface PremioCanjeadoResumen {
  nombre: string;
  puntos: number;
  cliente?: string;
  hora?: string;
}

export interface CierrePromocionesResumen {
  /** Nº de ventas del turno con algún descuento aplicado. */
  ventasConDescuento: number;
  /** Suma total descontada (global + por ítem + incentivo método). */
  totalDescuento: number;
  descuentoGlobal: number;
  descuentoItems: number;
  descuentoIncentivo: number;
  /** Líneas cobradas a precio mayorista y su valor. */
  lineasMayorista: number;
  totalMayorista: number;
  /** Líneas cobradas a precio VIP y su valor. */
  lineasVip: number;
  totalVip: number;
  /** Fidelización del turno. */
  puntosGanadosTurno: number;
  puntosCanjeadosTurno: number;
  /** Suma de saldos de los clientes con actividad en el turno (al cierre). */
  puntosCirculacion: number;
  porCliente: ClientePuntosResumen[];
  premiosCanjeados: PremioCanjeadoResumen[];
}

const round = (n: number): number => Math.max(0, Math.round(n));

function lineValor(linea: Venta["productos"][number]): number {
  return round((linea.precioUnitario ?? 0) * (linea.cantidad ?? 1));
}

function descuentoDeVenta(venta: Venta): {
  global: number;
  items: number;
  incentivo: number;
} {
  const d = venta.descuentos;
  if (!d) return { global: 0, items: 0, incentivo: 0 };
  return {
    global: round(
      d.breakdownSnapshot?.descuentoGlobal ?? d.descuentoGlobal ?? 0,
    ),
    items: round(
      d.breakdownSnapshot?.totalDescuentoItems ?? d.totalDescuentoItems ?? 0,
    ),
    incentivo: round(d.incentivoMetodoPago?.descuentoTotal ?? 0),
  };
}

export function buildVentasPromocionesResumen(
  ventas: Venta[],
): CierrePromocionesResumen {
  const resumen: CierrePromocionesResumen = {
    ventasConDescuento: 0,
    totalDescuento: 0,
    descuentoGlobal: 0,
    descuentoItems: 0,
    descuentoIncentivo: 0,
    lineasMayorista: 0,
    totalMayorista: 0,
    lineasVip: 0,
    totalVip: 0,
    puntosGanadosTurno: 0,
    puntosCanjeadosTurno: 0,
    puntosCirculacion: 0,
    porCliente: [],
    premiosCanjeados: [],
  };

  const clientes = new Map<string, ClientePuntosResumen>();
  const circulacion = new Map<string, number>();
  const activas = ventas.filter((v) => v.estado !== "anulada");

  for (const venta of activas) {
    // ── Descuentos ────────────────────────────────────────────────────────
    const desc = descuentoDeVenta(venta);
    const totalVenta = desc.global + desc.items + desc.incentivo;
    if (totalVenta > 0) {
      resumen.ventasConDescuento += 1;
      resumen.totalDescuento += totalVenta;
      resumen.descuentoGlobal += desc.global;
      resumen.descuentoItems += desc.items;
      resumen.descuentoIncentivo += desc.incentivo;
    }

    // ── Precios especiales por línea ──────────────────────────────────────
    for (const linea of venta.productos ?? []) {
      if (linea.esMayorista) {
        resumen.lineasMayorista += 1;
        resumen.totalMayorista += lineValor(linea);
      }
      if (linea.esVIP) {
        resumen.lineasVip += 1;
        resumen.totalVip += lineValor(linea);
      }
    }

    // ── Fidelización ──────────────────────────────────────────────────────
    const idCliente = venta.clientePuntosId;
    const ganados = venta.puntosGanados ?? 0;
    if (idCliente && venta.puntosNuevoSaldo != null) {
      circulacion.set(idCliente, venta.puntosNuevoSaldo);
    }
    if (idCliente && ganados > 0) {
      resumen.puntosGanadosTurno += ganados;
      const prev = clientes.get(idCliente);
      if (prev) {
        prev.puntosGanados += ganados;
        if (venta.puntosNuevoSaldo != null)
          prev.saldoNuevo = venta.puntosNuevoSaldo;
        if (venta.saldoAntesCanje != null && prev.saldoAntes == null) {
          prev.saldoAntes = venta.saldoAntesCanje;
        }
      } else {
        clientes.set(idCliente, {
          clientePuntosId: idCliente,
          nombre: venta.clientePuntosNombre ?? "Cliente",
          codigo: venta.clientePuntosCodigo,
          puntosGanados: ganados,
          saldoAntes: venta.saldoAntesCanje,
          saldoNuevo: venta.puntosNuevoSaldo,
        });
      }
    }

    for (const premio of venta.premiosCanjeados ?? []) {
      resumen.puntosCanjeadosTurno += premio.puntos;
      resumen.premiosCanjeados.push({
        nombre: premio.nombre,
        puntos: premio.puntos,
        cliente: venta.clientePuntosNombre,
        hora: venta.fecha,
      });
    }
  }

  resumen.porCliente = [...clientes.values()].sort((a, b) =>
    a.nombre.localeCompare(b.nombre, "es"),
  );
  resumen.puntosCirculacion = [...circulacion.values()].reduce(
    (s, saldo) => s + saldo,
    0,
  );

  return resumen;
}
